import { ChartFrame } from '../components/charts/ChartFrame';
import { TrendChart } from '../components/charts/TrendChart';
import { BarSeries } from '../components/charts/BarSeries';
import { StatTile } from '../components/tiles/StatTile';
import { compact, full, longDate, percent } from '../lib/format';
import { PLATFORM_COLORS, platformSeries, seriesFor, visibleSegments } from '../lib/platform';
import { PLATFORM_CLIENT, PLATFORM_LABELS, platformsFor, sumFor } from '../data/types';
import type { Platform, PlatformFilter, Snapshot } from '../data/types';

interface Props {
  snap: Snapshot;
  filter: PlatformFilter;
}

export function Platforms({ snap, filter }: Props) {
  const series = platformSeries(filter);
  const active = platformsFor(filter);
  const dau = snap.activeUsers.dau;
  const latest = dau[dau.length - 1];
  const total = active.reduce((sum, p) => sum + latest[p], 0) || 1;

  // Share over time answers "is Android taking over?" — the reason this page
  // exists. Indexed to 100% so every platform shares one axis.
  const mix = dau.map((d) => {
    const t = active.reduce((sum, p) => sum + d[p], 0) || 1;
    const row: Record<string, string | number> = { date: d.date };
    for (const p of active) row[p] = (d[p] / t) * 100;
    return row;
  });

  const themes = visibleSegments(snap.themes, filter);
  const sessions = snap.engagement.sessionLength;

  return (
    <>
      <div
        className="grid"
        style={{ gridTemplateColumns: `repeat(${Math.min(5, active.length)}, minmax(0, 1fr))` }}
      >
        {active.map((p) => (
          <StatTile
            key={p}
            label={`${PLATFORM_LABELS[p]} DAU`}
            value={full(latest[p])}
            history={dau.map((d) => d[p])}
            sparkColor={PLATFORM_COLORS[p]}
            foot={`${percent(latest[p] / total)} of selection · ${full(snap.liveNow[p])} online`}
          />
        ))}
      </div>

      <div className="grid">
        <div data-span style={{ gridColumn: 'span 7' }}>
          <ChartFrame
            title="Platform mix over time"
            subtitle="Share of daily actives — indexed to 100%"
            table={{
              columns: ['Date', ...active.map((p) => `${PLATFORM_LABELS[p]} %`)],
              rows: mix.map((m) => [
                longDate(String(m.date)),
                ...active.map((p) => `${Number(m[p]).toFixed(1)}%`),
              ]),
            }}
          >
            <TrendChart data={mix} series={series} height={272} valueFormat={(n) => `${n.toFixed(0)}%`} />
          </ChartFrame>
        </div>

        <div data-span style={{ gridColumn: 'span 5' }}>
          <ChartFrame
            title="Session length"
            subtitle="Distribution of session durations"
            table={{
              columns: ['Bucket', ...active.map((p) => PLATFORM_LABELS[p])],
              rows: sessions.map((s) => [s.bucket, ...active.map((p) => full(s[p]))]),
            }}
          >
            <BarSeries data={sessions} categoryKey="bucket" series={series} height={272} />
            <p className="card__sub" style={{ marginTop: 8 }}>
              Desktop sessions skew long; phone sessions skew short and frequent.
            </p>
          </ChartFrame>
        </div>

        {/* One card per platform's OS releases — a single stacked chart would
            put Windows 11 beside iOS 18, which are not comparable quantities. */}
        {active.map((p) => {
          const rows = snap.osVersions.filter((o) => o.platform === p);
          const osTotal = rows.reduce((sum, o) => sum + o[p], 0) || 1;
          return (
            <div data-span style={{ gridColumn: active.length <= 2 ? 'span 6' : 'span 4' }} key={p}>
              <ChartFrame
                title={`${PLATFORM_LABELS[p]} releases`}
                subtitle="Active users by OS version"
                table={{
                  columns: ['OS version', 'Users', 'Share'],
                  rows: rows.map((o) => [o.name, full(o[p]), percent(o[p] / osTotal)]),
                }}
              >
                <BarSeries
                  data={rows}
                  categoryKey="name"
                  series={[seriesFor(p as Platform)]}
                  layout="horizontal"
                  height={Math.max(160, rows.length * 44)}
                  categoryWidth={104}
                />
              </ChartFrame>
            </div>
          );
        })}

        <div data-span style={{ gridColumn: 'span 12' }}>
          <ChartFrame
            title="Theme selection"
            subtitle="Both clients ship the same nine themes — this is which ones users keep"
            table={{
              columns: ['Theme', ...active.map((p) => PLATFORM_LABELS[p]), 'Total'],
              rows: themes.map((t) => [
                t.name,
                ...active.map((p) => full(t[p])),
                full(sumFor(t, filter)),
              ]),
            }}
          >
            <BarSeries
              data={themes}
              categoryKey="name"
              series={series}
              stacked={series.length > 1}
              height={260}
              valueFormat={compact}
            />
          </ChartFrame>
        </div>
      </div>

      <p className="card__sub">
        {active.map((p) => `${PLATFORM_LABELS[p]} → ${PLATFORM_CLIENT[p]} client`).join(' · ')}
      </p>
    </>
  );
}
